<template lang="pug">
div
	mapbox(
		ref="map"
		style="height:500px"
		:layers="layers"
		:sources="sources"
		:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
		:mapOptions="customMapOptions ? customMapOptions : mapOptions"
		@map-styleload="mapStyle" v-on:map-load="mapLoaded")
		div(slot="popup")
			h3 {{popupData.name}}
			template(v-if="popupObject.name && popupObject.itemType === 'Skilift'")
				span Op {{formatDistance(popupObject.distance)}}
			template(v-else-if="popupObject.name && ['Skischool','Rental'].includes(popupObject.itemType)")
				span {{popupObject.partner.name}} op {{formatDistance(popupObject.distance)}}
				v-img(:src="popupObject.partner.image" max-height="200")
			template(v-else-if="popupData.__typename === 'Location'")
				v-img(:src="`${popupData.photo}?width=320`" max-height="200")
				div.d-flex(v-if="popupData.reviewCount")
					v-rating.inline-block(:value="popupData.rating" color="#ffb400" background-color="#ffb400" dense small)
					span.grey--text ({{popupData.reviewCount}})
			template(v-else-if="popupData.id === accommodation.id")
				span Huidige accommodatie
				v-img(:src="`${popupData.image}?width=320`" max-height="200")
			template(v-else-if="popupData.type === 'accommodation'")
				div {{formatDistance(popupData.distanceToCenter)}} van het centrum
				div vanaf {{ $n(popupData.price, 'currency', $i18n.locale) }}
				v-img(:src="`${popupData.image}?width=320`" max-height="200")
</template>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import useMap from '@/use/map';

const query = gql`
	query Accommodations(
		$id: ID!
		$filters: String
		$limit: Int
		$sort: AccommodationSortInput
	) {
		accommodation(id: $id) {
			id
			name
			type
			images
			location
			url
			rating
			stars
			isInresort
			views
			distanceToCenter
			pois {
				distance
				item {
					... on Skilift {
						id
						name
						type
						location
						geography {
							location {
								type
								coordinates
							}
						}
					}
					... on Rental {
						id
						name
						partner {
							id
							image
							name
							slug
						}
						geography {
							location {
								type
								coordinates
							}
						}
						url
					}
					... on Skischool {
						id
						name
						partner {
							id
							image
							name
							slug
						}
						geography {
							location {
								type
								coordinates
							}
						}
						url
					}
				}
			}
			resort {
				id
				name
				slug
				area {
					id
					name
					slug
				}
				locations(types: [restaurant, pisterestaurant, apresski, club]) {
					id
					name
					slug
					photos {
						url
						alt
					}
					properties
					reviews {
						voteCount
						average
					}
					geography {
						location {
							type
							coordinates
						}
					}
				}
			}
		}
		accommodationsNearby: accommodations(
			filters: $filters
			limit: $limit
			sort: $sort
		) {
			total
			nodes {
				id
				name
				type
				images
				location
				distanceToCenter
				variants {
					id
					name
					price
				}
			}
		}
	}
`;

export default {
	components: {
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	props: {
		accommodationId: {
			type: String,
			required: true
		},
		filterstring: {
			type: String,
			required: true
		}
	},
	setup() {
		const mapItems = ref([]);
		const { markers, sources, attachListeners, popupData, mapOptions } =
			useMap(mapItems);

		const layers = [
			{
				id: 'markers',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		return {
			mapItems,
			sources,
			layers,
			markers,
			attachListeners,
			popupData,
			mapOptions
		};
	},
	data() {
		return {
			accommodation: {},
			accommodationsNearby: [],
			customMapOptions: null,
			controller: new AbortController()
		};
	},
	async fetch() {
		const signal = this.controller.signal;

		await this.$graphClient
			.query({
				query,
				variables: {
					id: this.accommodationId,
					filters: this.filterstring,
					limit: 30,
					sort: {
						ascending: true,
						field: 'PRICE'
					}
				},
				context: {
					fetchOptions: {
						signal
					}
				}
			})
			.then(({ data }) => {
				this.accommodationsNearby = data.accommodationsNearby.nodes.filter(
					acco => acco.id !== this.accommodationId && acco.variants.length
				);
				this.accommodation = data.accommodation;

				this.drawMap();
			});
	},
	computed: {
		popupObject() {
			if (!this.accommodation || !this.accommodation.pois) {
				return false;
			}

			const item = this.accommodation.pois.find(
				poi => poi.item.id === this.popupData.id
			);

			return item && item.item
				? {
						...item.item,
						distance: item.distance,
						itemType: item.item.__typename
				  }
				: false;
		}
	},
	watch: {
		filterstring: '$fetch',
		accommodationId: '$fetch'
	},
	methods: {
		drawMap() {
			const pois = this.accommodation.pois.map(poi => {
				poi.item.icon =
					poi.item.__typename === 'Skilift'
						? `${poi.item.type}-marker`
						: `${poi.item.__typename.toLowerCase()}-marker`;
				poi.item.type = poi.item.__typename.toLowerCase();
				return poi.item;
			});

			const locations = this.accommodation.resort.locations.map(location => {
				location.icon =
					location.properties[0] === 'pisterestaurant'
						? 'restaurant-marker'
						: `${location.properties[0]}-marker`;
				return location;
			});

			const accommodation = {
				id: this.accommodation.id,
				icon: 'accommodation-marker-active',
				name: this.accommodation.name,
				image: this.accommodation.images.length
					? this.proxyImage(this.accommodation.images[0])
					: '',
				geography: {
					location: {
						coordinates: [
							this.accommodation.location.lon,
							this.accommodation.location.lat
						],
						type: 'Point'
					}
				}
			};

			const accosNearby = this.accommodationsNearby.map(acco => {
				return {
					id: acco.id,
					icon: 'accommodation-marker',
					name: acco.name,
					image: acco.images.length ? this.proxyImage(acco.images[0]) : '',
					type: 'accommodation',
					price: acco.variants[0].price,
					distanceToCenter: acco.distanceToCenter,
					geography: {
						location: {
							coordinates: [acco.location.lon, acco.location.lat],
							type: 'Point'
						}
					}
				};
			});

			this.mapItems = [accommodation, ...pois, ...locations, ...accosNearby];
			this.mapLoaded();
		},
		mouseClickMap(feature) {
			this.$emit('map-marker-click', feature);
		},
		mapStyle(map) {
			this.attachListeners(
				map,
				this.$refs.map,
				'markers',
				'geojson-markers',
				this.mouseClickMap
			);
		},
		formatDistance(distance) {
			if (distance < 10) {
				return '5m kruipen';
			} else if (distance < 200) {
				return Math.ceil(distance / 10) * 10 + 'm';
			} else if (distance < 500) {
				return Math.round(distance / 50) * 50 + 'm';
			} else if (distance < 1000) {
				return Math.round(distance / 100) * 100 + 'm';
			} else {
				return (distance / 1000).toFixed(1) + 'km';
			}
		},
		proxyImage(image) {
			const url = new URL(image);
			const imageWithoutProtocol = url.toString().replace(/https?:\/\//i, '');

			return `https://cdn.wintersport.nl/proxy/${imageWithoutProtocol}`;
		},
		abortFetchRequest() {
			this.controller.abort();
		},
		mapLoaded() {
			this.$refs.map.fitToCurrentBounds();
			if (this.accommodation.location) {
				this.$refs.map.map.setCenter(this.accommodation.location);
				if (this.$refs.map.map.getZoom() < 12) this.$refs.map.setZoom(12);
			}
		}
	}
};
</script>
